import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Button,
  alpha,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import EventAvailable from '@mui/icons-material/EventAvailable'
import EventBusy from '@mui/icons-material/EventBusy'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import Refresh from '@mui/icons-material/Refresh'
import Person from '@mui/icons-material/Person'
import Tv from '@mui/icons-material/Tv'
import Event from '@mui/icons-material/Event'
import Lock from '@mui/icons-material/Lock'
import LockOpen from '@mui/icons-material/LockOpen'
import AccessTime from '@mui/icons-material/AccessTime'
import People from '@mui/icons-material/People'
import Security from '@mui/icons-material/Security'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { useNavigate } from 'react-router-dom'
import ChamberRoomRosterDrawer from './ChamberRoomRosterDrawer'
import MentorsAndAuditorsDrawer from './MentorsAndAuditorsDrawer'

const ChamberRoomCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.light,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
}))

const RosterButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  fontSize: '0.8rem',
  fontWeight: 600,
}))

const StatusChip = styled(Chip)(({ theme, statustype }) => {
  const getColors = () => {
    switch (statustype) {
      case 'rostered':
        return {
          bg: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.main,
          border: theme.palette.success.light,
        }
      case 'not-rostered':
        return {
          bg: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[600],
          border: theme.palette.grey[400],
        }
      case 'enabled':
        return {
          bg: alpha(theme.palette.info.main, 0.1),
          color: theme.palette.info.main,
          border: theme.palette.info.light,
        }
      case 'disabled':
        return {
          bg: alpha(theme.palette.error.main, 0.1),
          color: theme.palette.error.main,
          border: theme.palette.error.light,
        }
      default:
        return {
          bg: theme.palette.grey[100],
          color: theme.palette.grey[600],
          border: theme.palette.grey[300],
        }
    }
  }
  const colors = getColors()
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
    fontWeight: 600,
    fontSize: '0.75rem',
  }
})

const hasEventTypeRosteringDetails = (room) => (
  Array.isArray(room?.eventTypeRosteringDetails) &&
  room.eventTypeRosteringDetails.some((detail) => detail?.eventType)
)

const isRoomRostered = (room) => {
  if (!hasEventTypeRosteringDetails(room)) {
    return room?.rostered === true
  }
  return room.eventTypeRosteringDetails.some((detail) => detail?.eventType && detail.rostered !== false)
}

// Auto-refresh interval: 30 seconds (as per API docs recommendation)
const STATUS_REFRESH_INTERVAL = 30000

export default function ChamberRoomsExpansion({ clinicBrandId, clinicLocationId, clinicLocationTitle, allMentors: allMentorsProp = [] }) {
  const navigate = useNavigate()
  const [chamberRooms, setChamberRooms] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false)
  const [error, setError] = useState(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [allMentorsState, setAllMentorsState] = useState([])
  const refreshIntervalRef = useRef(null)
  
  // Use prop if provided, otherwise use state
  const allMentors = allMentorsProp.length > 0 ? allMentorsProp : allMentorsState
  
  // Drawer state
  const [rosterDrawerOpen, setRosterDrawerOpen] = useState(false)
  const [mentorsDrawerOpen, setMentorsDrawerOpen] = useState(false)
  const [selectedChamberRoom, setSelectedChamberRoom] = useState(null)

  // Fetch all mentors (for resolving mentor and auditor profiles) - only if not provided as prop
  const fetchAllMentors = useCallback(async () => {
    // Skip if mentors are provided as prop
    if (allMentorsProp.length > 0) return
    
    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/mentors/list',
        {
          mentorEventTypes: 'therapy-session,consultation',
          sendAssociatedChambers: false,
        },
      )
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const mentors = getDataMapFromCommonResponse(resp)?.mentor || []
        setAllMentorsState(mentors)
      }
    } catch (error) {
      console.error('Error fetching mentors:', error)
    }
  }, [allMentorsProp.length])

  // Fetch full chamber rooms data (includes roster schedules, mentors, etc.)
  const fetchChamberRooms = useCallback(async () => {
    if (!clinicBrandId || !clinicLocationId) return

    setIsLoading(true)
    setError(null)

    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/csm/chamber-rooms',
        {
          clinicBrandId,
          clinicLocationId,
          includeRosterSchedule: true,
        },
      )

      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const rooms = getDataMapFromCommonResponse(resp)?.['chamber-room'] || []
        setChamberRooms(rooms)
        setHasLoaded(true)
      } else {
        setError('Failed to fetch chamber rooms')
      }
    } catch (err) {
      console.error('Error fetching chamber rooms:', err)
      setError('An error occurred while fetching chamber rooms')
    } finally {
      setIsLoading(false)
    }
  }, [clinicBrandId, clinicLocationId])

  // Fetch status data (lightweight, for occupancy/check-in status)
  const fetchRoomStatus = useCallback(async (showLoading = false) => {
    if (!clinicBrandId || !clinicLocationId) return

    if (showLoading) {
      setIsRefreshingStatus(true)
    }

    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/csm/chamber-rooms/status',
        {
          clinicBrandId,
          clinicLocationId,
        },
      )

      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const statusList = getDataMapFromCommonResponse(resp)?.['chamberRoomStatus'] || []
        
        // Merge status data into existing rooms
        setChamberRooms((prevRooms) => {
          return prevRooms.map((room) => {
            const status = statusList.find((s) => s.chamberRoomId === room.id)
            if (status) {
              return {
                ...room,
                // Override occupancy-related fields with fresh status data
                mentorPresent: status.mentorPresent,
                occupied: status.occupied,
                checkedInMentorEffectiveId: status.checkedInMentorEffectiveId,
                checkedInMentorEffectiveName: status.checkedInMentorEffectiveName,
                checkedInMentorId: status.checkedInMentorId,
                checkedInMentorName: status.checkedInMentorName,
                checkedInMethod: status.checkedInMethod,
                mentorLastCheckedIn: status.mentorLastCheckedIn,
                mentorLastCheckinRefreshed: status.mentorLastCheckinRefreshed,
                mentorLastCheckedOut: status.mentorLastCheckedOut,
              }
            }
            return room
          })
        })
      }
    } catch (err) {
      console.error('Error fetching room status:', err)
      // Don't show error for background refresh, only log it
    } finally {
      if (showLoading) {
        setIsRefreshingStatus(false)
      }
    }
  }, [clinicBrandId, clinicLocationId])

  // Initial load - fetch mentors (if not provided) and chamber rooms
  useEffect(() => {
    if (!hasLoaded) {
      const promises = [fetchChamberRooms()]
      // Only fetch mentors if not provided as prop
      if (allMentorsProp.length === 0) {
        promises.push(fetchAllMentors())
      }
      Promise.all(promises)
    }
  }, [fetchChamberRooms, fetchAllMentors, hasLoaded, allMentorsProp.length])

  // Auto-refresh status every 30 seconds
  useEffect(() => {
    if (hasLoaded && chamberRooms.length > 0) {
      // Initial status fetch after rooms are loaded
      fetchRoomStatus(false)

      // Set up auto-refresh interval
      refreshIntervalRef.current = setInterval(() => {
        fetchRoomStatus(false)
      }, STATUS_REFRESH_INTERVAL)

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current)
        }
      }
    }
  }, [hasLoaded, chamberRooms.length, fetchRoomStatus])

  // Manual refresh handler
  const handleManualRefresh = useCallback(() => {
    fetchRoomStatus(true)
  }, [fetchRoomStatus])

  const handleOpenRosterDrawer = (room) => {
    setSelectedChamberRoom(room)
    setRosterDrawerOpen(true)
  }

  const handleCloseRosterDrawer = () => {
    setRosterDrawerOpen(false)
    setSelectedChamberRoom(null)
  }

  const handleOpenMentorsDrawer = (room) => {
    setSelectedChamberRoom(room)
    setMentorsDrawerOpen(true)
  }

  const handleCloseMentorsDrawer = () => {
    setMentorsDrawerOpen(false)
    setSelectedChamberRoom(null)
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading chamber rooms...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="error" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={fetchChamberRooms}
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Box>
    )
  }

  if (chamberRooms.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <MeetingRoom sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No chamber rooms found for this location
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MeetingRoom color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Chamber Rooms
          </Typography>
          <Chip
            label={chamberRooms.length}
            size="small"
            color="primary"
            sx={{ height: 22 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Refresh occupancy status">
            <IconButton 
              size="small" 
              onClick={handleManualRefresh}
              disabled={isRefreshingStatus}
            >
              <Refresh 
                fontSize="small" 
                sx={{
                  animation: isRefreshingStatus ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh all data">
            <IconButton size="small" onClick={fetchChamberRooms}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Stack spacing={1}>
        {chamberRooms.map((room) => {
          const isRostered = isRoomRostered(room)
          const isEnabled = room?.enabled === true
          const hasRosterSchedule = !!room?.rosterSchedule
          const mentorPresent = room?.mentorPresent === true
          const isOccupied = room?.occupied === true
          const checkedInMentorEffectiveName = room?.checkedInMentorEffectiveName
          const checkedInMethod = room?.checkedInMethod
          const hasAUMHUMScreen = room?.hasAUMHUMScreenInstalled === true
          const description = room?.description
          const eventTypes = room?.primaryEventTypesForThisRoom?.length > 0
            ? room.primaryEventTypesForThisRoom
            : room?.eventTypesForThisRoom || []
          const accessScope = room?.accessAllowedTo
          const clinicBrandName = room?.clinicBrandName
          const clinicLocationName = room?.clinicLocationName
          const mentorsAndAuditors = room?.mentorsAndAuditors || []

          return (
            <ChamberRoomCard key={room.id} variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: mentorPresent
                          ? alpha('#f59e0b', 0.15)
                          : isRostered
                            ? alpha('#10b981', 0.1)
                            : alpha('#6b7280', 0.1),
                        flexShrink: 0,
                      }}
                    >
                      <MeetingRoom
                        sx={{
                          color: mentorPresent
                            ? '#f59e0b'
                            : isRostered
                              ? '#10b981'
                              : '#6b7280',
                          fontSize: 22,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {room.title || 'Unnamed Room'}
                        </Typography>
                        {mentorPresent && (
                          <Chip
                            size="small"
                            label="Occupied"
                            color="warning"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        )}
                        {!mentorPresent && isOccupied === false && (
                          <Chip
                            size="small"
                            label="Available"
                            color="success"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        ID: {room.id}
                      </Typography>
                      {description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {description}
                        </Typography>
                      )}
                      {(clinicBrandName || clinicLocationName) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {clinicBrandName && `${clinicBrandName}`}
                          {clinicBrandName && clinicLocationName && ' • '}
                          {clinicLocationName}
                        </Typography>
                      )}
                      {/* Occupancy Information */}
                      {mentorPresent && checkedInMentorEffectiveName && (
                        <Box sx={{ mt: 0.5, mb: 0.5, p: 0.75, borderRadius: 1, backgroundColor: alpha('#f59e0b', 0.08), border: `1px solid ${alpha('#f59e0b', 0.2)}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <Person sx={{ fontSize: 14, color: '#f59e0b' }} />
                            <Typography variant="caption" fontWeight={600} color="#f59e0b">
                              Occupied by: {checkedInMentorEffectiveName}
                            </Typography>
                          </Box>
                          {checkedInMethod && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 2.5 }}>
                              Method: {checkedInMethod === 'check-in-method-in-person' ? 'In-Person' : 'Remote'}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {!mentorPresent && (
                        <Box sx={{ mt: 0.5, mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Not currently occupied
                          </Typography>
                        </Box>
                      )}
                      {/* Mentors and Auditors Count */}
                      {mentorsAndAuditors.length > 0 && (
                        <Box sx={{ mt: 0.5, mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {mentorsAndAuditors.length} mentor{mentorsAndAuditors.length !== 1 ? 's' : ''} with access
                          </Typography>
                        </Box>
                      )}
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                        {hasAUMHUMScreen && (
                          <Tooltip title="AUMHUM Screen Installed">
                            <Chip
                              size="small"
                              icon={<Tv sx={{ fontSize: 12 }} />}
                              label="Screen"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Tooltip>
                        )}
                        {accessScope && (
                          <Tooltip title={`Access: ${accessScope}`}>
                            <Chip
                              size="small"
                              icon={accessScope === 'Explicitly-Allowed-List' ? <Lock sx={{ fontSize: 12 }} /> : <LockOpen sx={{ fontSize: 12 }} />}
                              label={accessScope.replace(/-/g, ' ')}
                              sx={{ height: 20, fontSize: '0.65rem', maxWidth: 150 }}
                            />
                          </Tooltip>
                        )}
                        {eventTypes.length > 0 && (
                          <Tooltip title={`Event Types: ${eventTypes.join(', ')}`}>
                            <Chip
                              size="small"
                              icon={<Event sx={{ fontSize: 12 }} />}
                              label={`${eventTypes.length} Event Type${eventTypes.length !== 1 ? 's' : ''}`}
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    <StatusChip
                      size="small"
                      icon={isEnabled ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                      label={isEnabled ? 'Enabled' : 'Disabled'}
                      statustype={isEnabled ? 'enabled' : 'disabled'}
                    />
                    <StatusChip
                      size="small"
                      icon={
                        isRostered ? (
                          <EventAvailable sx={{ fontSize: 14 }} />
                        ) : (
                          <EventBusy sx={{ fontSize: 14 }} />
                        )
                      }
                      label={isRostered ? 'Rostered' : 'Not Rostered'}
                      statustype={isRostered ? 'rostered' : 'not-rostered'}
                    />
                    {mentorsAndAuditors.length > 0 && (
                      <RosterButton
                        variant="outlined"
                        size="small"
                        startIcon={<People sx={{ fontSize: 16 }} />}
                        onClick={() => handleOpenMentorsDrawer(room)}
                        sx={{
                          borderColor: '#10b981',
                          color: '#10b981',
                          '&:hover': {
                            borderColor: '#059669',
                            backgroundColor: alpha('#10b981', 0.08),
                          },
                        }}
                      >
                        Mentors ({mentorsAndAuditors.length})
                      </RosterButton>
                    )}
                    {isRostered && (
                      <RosterButton
                        variant="contained"
                        size="small"
                        startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
                        onClick={() => handleOpenRosterDrawer(room)}
                        sx={{
                          backgroundColor: '#667eea',
                          '&:hover': {
                            backgroundColor: '#5a67d8',
                          },
                        }}
                      >
                        {hasRosterSchedule ? 'View Roster' : 'View Schedule'}
                      </RosterButton>
                    )}
                    <RosterButton
                      variant="outlined"
                      size="small"
                      startIcon={<Security sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        navigate(
                          `/support-home/practitioner-management/auditors/audit?chamberRoomIds=${room.id}`
                        )
                      }}
                      sx={{
                        borderColor: '#8b5cf6',
                        color: '#8b5cf6',
                        '&:hover': {
                          borderColor: '#7c3aed',
                          backgroundColor: alpha('#8b5cf6', 0.08),
                        },
                      }}
                    >
                      Audit Requests
                    </RosterButton>
                  </Stack>
                </Box>
              </CardContent>
            </ChamberRoomCard>
          )
        })}
      </Stack>

      {/* Roster Drawer */}
      <ChamberRoomRosterDrawer
        open={rosterDrawerOpen}
        onClose={handleCloseRosterDrawer}
        chamberRoom={selectedChamberRoom}
        clinicLocationTitle={clinicLocationTitle}
      />

      {/* Mentors and Auditors Drawer */}
      <MentorsAndAuditorsDrawer
        open={mentorsDrawerOpen}
        onClose={handleCloseMentorsDrawer}
        chamberRoom={selectedChamberRoom}
        clinicLocationTitle={clinicLocationTitle}
        allMentors={allMentors}
      />
    </Box>
  )
}
